// src/services/auth/hooks.ts

import { useContext, useEffect, useState } from "react";
import type { AuthService } from "./AuthService";
import { AuthServiceContext } from "./context";

/**
 * Hook to access the AuthService from any component
 *
 * @throws Error if used outside of AuthServiceProvider
 * @returns AuthService instance
 *
 * @example
 * const MyComponent = () => {
 *   const authService = useAuthService();
 *
 *   const handleLogin = async () => {
 *     await authService.login();
 *   };
 *
 *   return <button onClick={handleLogin}>Login</button>;
 * };
 */
export const useAuthService = (): AuthService => {
  const service = useContext(AuthServiceContext);

  if (!service) {
    throw new Error("useAuthService must be used within AuthServiceProvider");
  }

  return service;
};

/**
 * Hook to access authentication state from AuthService
 * This is a convenience hook that subscribes to auth state changes
 *
 * @returns Current authentication state
 *
 * @example
 * const MyComponent = () => {
 *   const { isAuthenticated, isLoading, user } = useAuthState();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (!isAuthenticated) return <div>Not logged in</div>;
 *
 *   return <div>Welcome, {user?.name}!</div>;
 * };
 */
export const useAuthState = () => {
  const service = useAuthService();
  const [user, setUser] = useState(service.getUser());
  const [isLoading, setIsLoading] = useState(service.isLoading());
  const [isLoggingOut, setIsLoggingOut] = useState(service.isLoggingOut());

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = service.onAuthStateChange((newUser) => {
      setUser(newUser);
      setIsLoading(service.isLoading());
      setIsLoggingOut(service.isLoggingOut());
    });

    // Update initial state
    setUser(service.getUser());
    setIsLoading(service.isLoading());
    setIsLoggingOut(service.isLoggingOut());

    return unsubscribe;
  }, [service]);

  return {
    isAuthenticated: service.isAuthenticated(),
    isLoading,
    isLoggingOut,
    user,
    error: service.getError(),
  };
};
